(() => {
  'use strict';

  if (window.SMIBMusic) return;

  const STORAGE_TRACK = 'smib_music_track_v4';
  const STORAGE_TIME = 'smib_music_time_v4';
  const STORAGE_VOLUME = 'smib_music_volume_v4';
  const STORAGE_WANTS_PLAY = 'smib_music_wants_play_v4';
  const SAVE_INTERVAL = 500;

  let player = null;
  let audio = null;
  let tracks = [];
  let currentIndex = 0;
  let wantsPlay = readPersistent(STORAGE_WANTS_PLAY, readPersistent('smib_music_wants_play_v3', '0')) === '1';
  let lastSave = 0;
  let isSeeking = false;
  let globalEventsBound = false;

  function readSession(key, fallback = '') {
    try {
      const value = sessionStorage.getItem(key);
      return value === null ? fallback : value;
    } catch (_error) {
      return fallback;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, String(value));
    } catch (_error) {}
  }

  function readPersistent(key, fallback = '') {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) return value;
    } catch (_error) {}

    return readSession(key, fallback);
  }

  function writePersistent(key, value) {
    try {
      localStorage.setItem(key, String(value));
      return;
    } catch (_error) {}

    writeSession(key, value);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function formatTime(seconds) {
    const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainder = safeSeconds % 60;
    return `${minutes}:${String(remainder).padStart(2, '0')}`;
  }

  function getOpenButtons() {
    return Array.from(document.querySelectorAll('[data-smib-music-open]'));
  }

  function getElements() {
    if (!player) return {};

    return {
      panel: player.querySelector('.smib-player__panel'),
      playButton: player.querySelector('[data-smib-player-play]'),
      previousButton: player.querySelector('[data-smib-player-previous]'),
      nextButton: player.querySelector('[data-smib-player-next]'),
      progress: player.querySelector('[data-smib-player-progress]'),
      volume: player.querySelector('[data-smib-player-volume]'),
      currentTime: player.querySelector('[data-smib-player-current-time]'),
      duration: player.querySelector('[data-smib-player-duration]'),
      status: player.querySelector('[data-smib-player-status]'),
      title: player.querySelector('[data-smib-player-title]'),
      artist: player.querySelector('[data-smib-player-artist]'),
      artwork: player.querySelector('[data-smib-player-artwork]'),
      playIcon: player.querySelector('[data-smib-play-icon]'),
      pauseIcon: player.querySelector('[data-smib-pause-icon]'),
    };
  }

  function collectTracks() {
    tracks = Array.from(player?.querySelectorAll('[data-smib-track]') || [])
      .map((element) => ({
        element,
        src: element.dataset.audioUrl?.trim() || '',
        title: element.dataset.title?.trim() || 'Untitled',
        artist: element.dataset.artist?.trim() || 'SMIB',
        artwork: element.dataset.artworkUrl?.trim() || '',
      }))
      .filter((track) => track.src);
  }

  function saveState(force = false) {
    if (!audio) return;

    const now = Date.now();
    if (!force && now - lastSave < SAVE_INTERVAL) return;
    lastSave = now;

    writePersistent(STORAGE_TRACK, currentIndex);
    writePersistent(STORAGE_VOLUME, audio.volume);

    if (Number.isFinite(audio.currentTime)) {
      writeSession(STORAGE_TIME, audio.currentTime);
    }
  }

  function restoreVolume() {
    if (!audio || !player) return;

    const defaultVolume = Number.parseFloat(player.dataset.defaultVolume || '0.65');
    const stored = Number.parseFloat(readPersistent(STORAGE_VOLUME, ''));
    const volume = Number.isFinite(stored) ? stored : defaultVolume;
    audio.volume = clamp(volume, 0, 1);

    const { volume: volumeInput } = getElements();
    if (volumeInput) volumeInput.value = String(Math.round(audio.volume * 100));
  }

  function updateTrackUi() {
    const track = tracks[currentIndex];
    const playing = Boolean(audio && !audio.paused && !audio.ended);
    const elements = getElements();

    tracks.forEach((candidate, index) => {
      const active = index === currentIndex;
      candidate.element.classList.toggle('is-active', active);
      candidate.element.classList.toggle('is-playing', active && playing);
      candidate.element.setAttribute('aria-current', active ? 'true' : 'false');
      const state = candidate.element.querySelector('.smib-player__track-state');
      if (state) state.textContent = active && playing ? 'PLAYING' : active ? 'SELECTED' : 'PLAY';
    });

    getOpenButtons().forEach((button) => {
      button.classList.toggle('is-playing', playing);
      button.setAttribute('aria-expanded', player?.classList.contains('is-open') ? 'true' : 'false');
    });

    if (elements.playButton) {
      elements.playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
      elements.playButton.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    }

    if (elements.playIcon) elements.playIcon.hidden = playing;
    if (elements.pauseIcon) elements.pauseIcon.hidden = !playing;

    if (!track) {
      if (elements.status) elements.status.textContent = 'NO TRACKS';
      return;
    }

    if (elements.status) elements.status.textContent = playing ? 'NOW PLAYING' : 'READY';
    if (elements.title) elements.title.textContent = track.title;
    if (elements.artist) elements.artist.textContent = track.artist;

    if (elements.artwork) {
      if (track.artwork) {
        if (elements.artwork.src !== track.artwork) elements.artwork.src = track.artwork;
        elements.artwork.alt = `${track.title} artwork`;
        elements.artwork.hidden = false;
      } else {
        elements.artwork.removeAttribute('src');
        elements.artwork.alt = '';
        elements.artwork.hidden = true;
      }
    }
  }

  function updateTimeUi() {
    if (!audio || isSeeking) return;

    const elements = getElements();
    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    const currentTime = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;

    if (elements.currentTime) elements.currentTime.textContent = formatTime(currentTime);
    if (elements.duration) elements.duration.textContent = formatTime(duration);

    if (elements.progress) {
      const ratio = duration > 0 ? currentTime / duration : 0;
      elements.progress.value = String(Math.round(clamp(ratio, 0, 1) * 1000));
    }
  }

  function setSource(index, options = {}) {
    if (!audio || !tracks.length) return false;

    const normalizedIndex = ((Number(index) % tracks.length) + tracks.length) % tracks.length;
    const track = tracks[normalizedIndex];
    const changed = currentIndex !== normalizedIndex || audio.getAttribute('src') !== track.src;
    const savedTime = options.restoreTime
      ? Number.parseFloat(readSession(STORAGE_TIME, readSession('smib_music_time_v3', '0')))
      : 0;

    if (changed) {
      if (!options.restoreTime) saveState(true);
      currentIndex = normalizedIndex;
      audio.src = track.src;
      audio.load();
      writePersistent(STORAGE_TRACK, currentIndex);
      if (!options.restoreTime) writeSession(STORAGE_TIME, '0');
    }

    if (options.restoreTime) {
      if (Number.isFinite(savedTime) && savedTime > 0) {
        const restore = () => {
          try {
            const max = Number.isFinite(audio.duration) && audio.duration > 0 ? Math.max(0, audio.duration - 0.25) : savedTime;
            audio.currentTime = Math.min(savedTime, max);
          } catch (_error) {}
          writeSession(STORAGE_TIME, savedTime);
          updateTimeUi();
        };

        if (audio.readyState >= 1) restore();
        else audio.addEventListener('loadedmetadata', restore, { once: true });
      }
    }

    updateTrackUi();
    updateTimeUi();
    return true;
  }

  async function play() {
    if (!audio || !tracks.length) return;

    if (!audio.src) setSource(currentIndex);

    wantsPlay = true;
    writePersistent(STORAGE_WANTS_PLAY, '1');

    try {
      await audio.play();
    } catch (_error) {
      // Browsers can block autoplay until the visitor interacts with the page.
      // Keep the play intent so the next real interaction can unlock playback.
    }

    updateTrackUi();
  }

  function pause() {
    if (!audio) return;

    wantsPlay = false;
    writePersistent(STORAGE_WANTS_PLAY, '0');
    saveState(true);
    audio.pause();
    updateTrackUi();
  }

  function togglePlayback() {
    if (!audio || !tracks.length) return;
    if (audio.paused) play();
    else pause();
  }

  function playTrack(index) {
    const changed = setSource(index);
    if (!changed) return;
    play();
  }

  function next() {
    if (!tracks.length) return;
    const shouldContinue = wantsPlay || (audio && !audio.paused);
    setSource(currentIndex + 1);
    if (shouldContinue) play();
  }

  function previous() {
    if (!tracks.length) return;

    if (audio && audio.currentTime > 4) {
      audio.currentTime = 0;
      updateTimeUi();
      return;
    }

    const shouldContinue = wantsPlay || (audio && !audio.paused);
    setSource(currentIndex - 1);
    if (shouldContinue) play();
  }

  function openPlayer() {
    if (!player) return;
    player.classList.add('is-open');
    document.body.classList.add('smib-player-open');
    getOpenButtons().forEach((button) => button.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => player.querySelector('.smib-player__panel')?.focus({ preventScroll: true }));
  }

  function closePlayer() {
    if (!player) return;
    player.classList.remove('is-open');
    document.body.classList.remove('smib-player-open');
    getOpenButtons().forEach((button) => button.setAttribute('aria-expanded', 'false'));
  }

  function togglePlayer() {
    if (!player) return;
    if (player.classList.contains('is-open')) closePlayer();
    else openPlayer();
  }


  function unlockPlayback(event) {
    if (!wantsPlay || !audio?.paused || !tracks.length) return;
    if (event.target?.closest?.('[data-smib-player]')) return;
    play();
  }

  function bindAudioEvents() {
    if (!audio || audio.dataset.smibBound === 'true') return;
    audio.dataset.smibBound = 'true';

    audio.addEventListener('play', updateTrackUi);
    audio.addEventListener('pause', () => {
      saveState(true);
      updateTrackUi();
    });
    audio.addEventListener('timeupdate', () => {
      saveState(false);
      updateTimeUi();
    });
    audio.addEventListener('loadedmetadata', updateTimeUi);
    audio.addEventListener('durationchange', updateTimeUi);
    audio.addEventListener('ended', () => {
      writeSession(STORAGE_TIME, '0');
      next();
    });
  }

  function bindGlobalEvents() {
    if (globalEventsBound) return;
    globalEventsBound = true;

    document.addEventListener('click', (event) => {
      const openButton = event.target?.closest?.('[data-smib-music-open]');
      if (openButton) {
        event.preventDefault();
        event.stopPropagation();
        togglePlayer();
        return;
      }

      const closeButton = event.target?.closest?.('[data-smib-player-close]');
      if (closeButton) {
        closePlayer();
        return;
      }

      const playButton = event.target?.closest?.('[data-smib-player-play]');
      if (playButton) {
        togglePlayback();
        return;
      }

      const previousButton = event.target?.closest?.('[data-smib-player-previous]');
      if (previousButton) {
        previous();
        return;
      }

      const nextButton = event.target?.closest?.('[data-smib-player-next]');
      if (nextButton) {
        next();
        return;
      }

      const track = event.target?.closest?.('[data-smib-track]');
      if (track) playTrack(Number(track.dataset.smibTrackIndex));
    });

    document.addEventListener('input', (event) => {
      const progress = event.target?.closest?.('[data-smib-player-progress]');
      if (progress && audio) {
        isSeeking = true;
        const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
        const requested = (Number(progress.value) / 1000) * duration;
        const { currentTime } = getElements();
        if (currentTime) currentTime.textContent = formatTime(requested);
        return;
      }

      const volume = event.target?.closest?.('[data-smib-player-volume]');
      if (volume && audio) {
        audio.volume = clamp(Number(volume.value) / 100, 0, 1);
        writePersistent(STORAGE_VOLUME, audio.volume);
      }
    });

    document.addEventListener('change', (event) => {
      const progress = event.target?.closest?.('[data-smib-player-progress]');
      if (!progress || !audio) return;

      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      if (duration > 0) audio.currentTime = (Number(progress.value) / 1000) * duration;
      isSeeking = false;
      updateTimeUi();
      saveState(true);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && player?.classList.contains('is-open')) {
        closePlayer();
        return;
      }
      unlockPlayback(event);
    });

    document.addEventListener('pointerdown', unlockPlayback, { capture: true, passive: true });
    document.addEventListener('touchstart', unlockPlayback, { capture: true, passive: true });

    document.addEventListener('smib:page:loaded', updateTrackUi);
    document.addEventListener('shopify:section:load', (event) => {
      const nextPlayer = event.target?.matches?.('[data-smib-player]')
        ? event.target
        : event.target?.querySelector?.('[data-smib-player]');
      if (nextPlayer) start(true);
      else updateTrackUi();
    });

    document.addEventListener('shopify:section:select', (event) => {
      const selectedPlayer = event.target?.matches?.('[data-smib-player]')
        ? event.target
        : event.target?.querySelector?.('[data-smib-player]');
      if (!selectedPlayer) return;
      start(true);
      openPlayer();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const selectedTrack = event.target?.matches?.('[data-smib-track]')
        ? event.target
        : event.target?.querySelector?.('[data-smib-track]');
      if (!selectedTrack) return;

      const selectedPlayer = selectedTrack.closest('[data-smib-player]');
      if (selectedPlayer && selectedPlayer !== player) start(true);
      openPlayer();

      const index = Number(selectedTrack.dataset.smibTrackIndex);
      if (Number.isFinite(index)) setSource(index);
      selectedTrack.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    window.addEventListener('pagehide', () => saveState(true));
    window.addEventListener('beforeunload', () => saveState(true));
  }

  function start(force = false) {
    const nextPlayer = document.querySelector('[data-smib-player]');
    if (!nextPlayer) return;
    if (player === nextPlayer && audio && !force) return;

    player = nextPlayer;
    audio = player.querySelector('[data-smib-persistent-audio]');
    if (!(audio instanceof HTMLAudioElement)) return;

    collectTracks();
    restoreVolume();

    const storedIndex = Number.parseInt(readPersistent(STORAGE_TRACK, '0'), 10);
    currentIndex = Number.isFinite(storedIndex) ? clamp(storedIndex, 0, Math.max(0, tracks.length - 1)) : 0;

    if (tracks.length) setSource(currentIndex, { restoreTime: true });
    updateTrackUi();
    updateTimeUi();

    bindAudioEvents();
    bindGlobalEvents();

    if (wantsPlay) play();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  } else {
    start();
  }

  window.SMIBMusic = Object.freeze({
    open: openPlayer,
    close: closePlayer,
    play,
    pause,
    toggle: togglePlayback,
    next,
    previous,
    playTrack,
    get audio() {
      return audio;
    },
    get currentTrack() {
      return tracks[currentIndex] || null;
    },
  });
})();
